import { DI } from '@intigral/constant/config/DIContainer';
import { IGetMoviesUsecase } from '../../core/usecases/mpx/GetMoviesUseCase';
import { container } from '../../DataDomain';
import {
    IMpxAllAssetsParams,
    IMpxBiographyResponse,
    IMpxFilmographyResponse,
    IMpxVodResponse,
    IGetCatchUpEpisodeListParams,
    IGetCatchUpEpisodeDetailParams,
    IGetCatchUpEpisodeDetailResponse,
    IGetPPVFeedParams,
    IContentDetailsResponse,
} from '../../entity/interface/iMpxVodResponse';
import { IGetSeriesUseCase } from '../../core/usecases/mpx/GetSeriesUseCase';
import { IGetEpisodesUseCase } from '../../core/usecases/mpx/GetEpisodesUseCase';
import { IGetFeedUseCase } from '../../core/usecases/mpx/GetFeedUseCase';
import { IGetBiographyUseCase } from '../../core/usecases/mpx/GetBiographyUseCase';
import { IGetFilmographyUseCase } from '../../core/usecases/mpx/GetFilmographyUseCase';
import { IGetProductsUseCase } from '../../core/usecases/mpx/GetProductsUseCase';
import { IGetMpxAllAssetsUseCase } from '../../core/usecases/mpx/GetMpxAllAssetsUseCase';
import { IGetMpxCastCrewUseCase } from '../../core/usecases/mpx/GetMpxCastCrewUseCase';
import { IGetSeasonsUseCase } from '../../core/usecases/mpx/GetSeasonsUseCase';
import { IGetChannelCategoriesUseCase } from '../../core/usecases/mpx/GetChannelCategoriesUseCase';
import { IGetChannelListByCategoryUseCase } from '../../core/usecases/mpx/GetChannelListByCategoryUseCase';
import { IChannelCategoriesResponse } from '../../entity/interface/iChannelCategoriesResponse';
import { IChannelListByCategoryResponse } from '../../entity/interface/iChannelListByCategoryResponse';
import { IGetCatchUpEpisodesUseCase } from '../../core/usecases/mpx/GetCatchUpEpisodesUseCase';
import { IGetCatchUpEpisodeDetailUseCase } from '../../core/usecases/mpx/GetCatchUpEpisodeDetailUseCase';
import { IGetPPVFeedUseCase } from '../../core/usecases/mpx/GetPPVFeedUseCase';
import { IGetMixedCatchUpEpisodesUseCase } from '../../core/usecases/mpx/GetMixedCatchUpEpisodesUseCase';
import { IGetContentDetailsUseCase } from '../../core/usecases/mpx/GetContentDetailsUseCase';

export interface IMpxService {
    getMovies(feed: string | null, filters: { [key: string]: string | number | boolean }): Promise<IMpxVodResponse>;
    getSeries(feed: string | null, filters: { [key: string]: string | number | boolean }): Promise<IMpxVodResponse>;
    getEpisodes(byTvSeasonId: string | null, byCustomValue: any): Promise<IMpxVodResponse>;
    getFeed(feed: string, filters: { [key: string]: string | number | boolean }): Promise<any>;
    getBiography(feed: string): Promise<IMpxBiographyResponse>;
    getFilmography(byCustomValue: string, personId: string): Promise<IMpxFilmographyResponse>;
    getProducts(feed: string, filters: { [key: string]: string | number | boolean }): Promise<unknown>;
    getMpxAllAssets(params: IMpxAllAssetsParams): Promise<any>;
    getMpxCastCrew(byCredits: string): Promise<any>;
    getSeasons(bySeriesId: string, byCustomValue: string): Promise<any>;
    getChannelCategories(guids: string, country: string, isKidsMode: boolean): Promise<IChannelCategoriesResponse>;
    getChannelListByCategory(category: string): Promise<IChannelListByCategoryResponse>;
    getCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse>;
    getMixedCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse>;
    getCatchUpEpisodeDetail(params: IGetCatchUpEpisodeDetailParams): Promise<IGetCatchUpEpisodeDetailResponse>;
    getPPVFeed(params: IGetPPVFeedParams): Promise<any>;
    getContentDetails(guid: string): Promise<IContentDetailsResponse>;
}

export class MpxService implements IMpxService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get getMoviesUseCase(): IGetMoviesUsecase {
        return container.resolve<IGetMoviesUsecase>(DI.USECASES.IGetMoviesUseCase);
    }

    private get getSeriesUseCase(): IGetSeriesUseCase {
        return container.resolve<IGetSeriesUseCase>(DI.USECASES.IGetSeriesUseCase);
    }

    private get getEpisodesUseCase(): IGetEpisodesUseCase {
        return container.resolve<IGetEpisodesUseCase>(DI.USECASES.IGetEpisodesUseCase);
    }

    private get getFeedUseCase(): IGetFeedUseCase {
        return container.resolve<IGetFeedUseCase>(DI.USECASES.IGetFeedUseCase);
    }

    private get getBiographyUseCase(): IGetBiographyUseCase {
        return container.resolve<IGetBiographyUseCase>(DI.USECASES.IGetBiographyUseCase);
    }

    private get getFilmographyUseCase(): IGetFilmographyUseCase {
        return container.resolve<IGetFilmographyUseCase>(DI.USECASES.IGetFilmographyUseCase);
    }

    private get getProductsUseCase(): IGetProductsUseCase {
        return container.resolve<IGetProductsUseCase>(DI.USECASES.IGetProductsUseCase);
    }

    private get getMpxAllAssetsUseCase(): IGetMpxAllAssetsUseCase {
        return container.resolve<IGetMpxAllAssetsUseCase>(DI.USECASES.IGetMpxAllAssetsUseCase);
    }
    private get getPPVFeedUseCase(): IGetPPVFeedUseCase {
        return container.resolve<IGetPPVFeedUseCase>(DI.USECASES.IGetPPVFeedUseCase);
    }

    private get getMpxCastCrewUseCase(): IGetMpxCastCrewUseCase {
        return container.resolve<IGetMpxCastCrewUseCase>(DI.USECASES.IGetMpxCastCrewUseCase);
    }

    private get getSeasonsUseCase(): IGetSeasonsUseCase {
        return container.resolve<IGetSeasonsUseCase>(DI.USECASES.IGetSeasonsUseCase);
    }

    private get getChannelCategoriesUseCase(): IGetChannelCategoriesUseCase {
        return container.resolve<IGetChannelCategoriesUseCase>(DI.USECASES.IGetChannelCategoriesUseCase);
    }

    private get getChannelListByCategoryUseCase(): IGetChannelListByCategoryUseCase {
        return container.resolve<IGetChannelListByCategoryUseCase>(DI.USECASES.IGetChannelListByCategoryUseCase);
    }

    private get getCatchUpEpisodeListUseCase(): IGetCatchUpEpisodesUseCase {
        return container.resolve<IGetCatchUpEpisodesUseCase>(DI.USECASES.IGetCatchUpEpisodesUseCase);
    }

    private get getMixedCatchUpEpisodeListUseCase(): IGetMixedCatchUpEpisodesUseCase {
        return container.resolve<IGetMixedCatchUpEpisodesUseCase>(DI.USECASES.IGetMixedCatchUpEpisodesUseCase);
    }

    private get getCatchUpEpisodeDetailUseCase(): IGetCatchUpEpisodeDetailUseCase {
        return container.resolve<IGetCatchUpEpisodeDetailUseCase>(DI.USECASES.IGetCatchUpEpisodeDetailUseCase);
    }

    private get getContentDetailsUseCase(): IGetContentDetailsUseCase {
        return container.resolve<IGetContentDetailsUseCase>(DI.USECASES.IGetContentDetailsUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async getMovies(
        feed: string | null,
        filters: { [key: string]: string | number | boolean }
    ): Promise<IMpxVodResponse> {
        return await this.getMoviesUseCase.execute(feed, filters);
    }

    async getSeries(
        feed: string | null,
        filters: { [key: string]: string | number | boolean }
    ): Promise<IMpxVodResponse> {
        return await this.getSeriesUseCase.execute(feed, filters);
    }

    async getEpisodes(byTvSeasonId: string, byCustomValue: string): Promise<IMpxVodResponse> {
        return await this.getEpisodesUseCase.execute(byTvSeasonId, byCustomValue);
    }

    async getFeed(feed: string, filters: { [key: string]: string | number | boolean }): Promise<any> {
        return await this.getFeedUseCase.execute(feed, filters);
    }

    async getBiography(feed: string): Promise<IMpxBiographyResponse> {
        return await this.getBiographyUseCase.execute(feed);
    }

    async getFilmography(byCustomValue: string, personId: string): Promise<IMpxFilmographyResponse> {
        return await this.getFilmographyUseCase.execute(byCustomValue, personId);
    }

    async getProducts(feed: string, filters: { [key: string]: string | number | boolean }): Promise<unknown> {
        return await this.getProductsUseCase.execute(feed, filters);
    }

    async getMpxAllAssets(params: IMpxAllAssetsParams): Promise<any> {
        return await this.getMpxAllAssetsUseCase.execute(params);
    }
    async getPPVFeed(params: IMpxAllAssetsParams): Promise<any> {
        return await this.getPPVFeedUseCase.execute(params);
    }

    async getMpxCastCrew(byCredits: string): Promise<any> {
        return await this.getMpxCastCrewUseCase.execute(byCredits);
    }
    async getSeasons(bySeriesId: string, byCustomValue: string): Promise<any> {
        return await this.getSeasonsUseCase.execute(bySeriesId, byCustomValue);
    }
    async getChannelCategories(
        guids: string,
        country: string,
        isKidsMode: boolean
    ): Promise<IChannelCategoriesResponse> {
        return await this.getChannelCategoriesUseCase.execute(guids, country, isKidsMode);
    }
    async getChannelListByCategory(category: string): Promise<IChannelListByCategoryResponse> {
        return await this.getChannelListByCategoryUseCase.execute(category);
    }
    async getCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse> {
        return await this.getCatchUpEpisodeListUseCase.execute(params);
    }
    async getMixedCatchUpEpisodeList(params: IGetCatchUpEpisodeListParams): Promise<IMpxVodResponse> {
        return await this.getMixedCatchUpEpisodeListUseCase.execute(params);
    }
    async getCatchUpEpisodeDetail(params: IGetCatchUpEpisodeDetailParams): Promise<IGetCatchUpEpisodeDetailResponse> {
        return await this.getCatchUpEpisodeDetailUseCase.execute(params);
    }
    async getContentDetails(guid: string): Promise<IContentDetailsResponse> {
        return await this.getContentDetailsUseCase.execute(guid);
    }
}
